import React, { useRef, useState, useMemo } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tag } from 'primereact/tag';

// Keep identity visible below the horse while the existing button retains its accessible summary.
const RaceHorseMarker = ({ person, size, facingRight = true }) => (
    <span className="okr-race-horse" aria-hidden="true" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: `${size + 4}px`, filter: person.reachedGoal ? 'drop-shadow(0 0 3px #fbbf24)' : undefined }}>
        <span className="okr-team-horse" data-direction={facingRight ? 'right' : 'left'} style={{ display: 'block', fontSize: `${size}px`, lineHeight: 1, transform: facingRight ? 'scaleX(-1)' : 'none', transformOrigin: 'center' }}>🐎</span>
        <span style={{ marginTop: '-4px', padding: '1px 4px', borderRadius: '4px', background: person.color, color: '#fff', fontSize: size < 28 ? '0.7rem' : '0.8rem', fontWeight: 800, lineHeight: 1, border: person.reachedGoal ? '2px solid #fbbf24' : '1px solid #fff', boxShadow: '0 1px 3px rgba(15,23,42,.2)' }}>{person.initials}</span>
    </span>
);

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

const getId = (value) => value?._id || value?.id || value || '';

const normalizeId = (value) => {
    const id = getId(value);
    return id ? String(id) : '';
};

const formatNumber = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number.toLocaleString('es-PA', { maximumFractionDigits: 2 }) : '0';
};

const getFiniteNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

const getTokenPositionPoints = (token, cycle) => {
    const target = getFiniteNumber(cycle?.pointsBase, 1000) || 1000;
    const accumulatedPoints = getFiniteNumber(token?.accumulatedPoints, 0);
    return Math.min(Math.max(accumulatedPoints, 0), target);
};

const warnInDevelopment = (condition, message, context) => {
    if (process.env.NODE_ENV === 'development' && condition) {
        // eslint-disable-next-line no-console
        console.warn(`[OKR Race Board] ${message}`, context);
    }
};

const isObjectIdString = (value) => typeof value === 'string' && OBJECT_ID_PATTERN.test(value);

const getObjectUserName = (user) => {
    if (!user || typeof user !== 'object') return '';
    if (user.fullName) return user.fullName;
    if (user.name && !isObjectIdString(user.name)) return user.name;
    const name = `${user.firstName || user.firstname || ''} ${user.lastName || user.lastname || ''}`.trim();
    return name || user.displayName || user.userName || user.username || user.email || '';
};

const getUserDisplayName = (userOrId, usersMap = {}) => {
    if (!userOrId) return 'Usuario sin nombre';

    if (typeof userOrId === 'string') {
        const mappedUser = usersMap[userOrId];
        return getObjectUserName(mappedUser) || 'Usuario sin nombre';
    }

    const directName = getObjectUserName(userOrId);
    if (directName) return directName;

    const mappedUser = usersMap[normalizeId(userOrId)];
    return getObjectUserName(mappedUser) || 'Usuario sin nombre';
};

const getInitials = (name) => {
    if (!name || name === 'Usuario sin nombre') return '?';
    const parts = String(name)
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const TEAM_COLORS = ['#1d4ed8', '#047857', '#b45309', '#7e22ce', '#b91c1c', '#0e7490', '#475569', '#be185d', '#0f766e', '#4338ca', '#c2410c', '#6d28d9'];
const BOARD_STEP = 25;
const BOARD_COLUMNS = 7;
const CELL_PALETTE = ['#fef3c7', '#dcfce7', '#ffedd5', '#fefce8', '#ecfdf5'];

const chunk = (items, size) => {
    const rows = [];
    for (let index = 0; index < items.length; index += size) {
        rows.push(items.slice(index, index + size));
    }
    return rows;
};

const getPaceStatus = (person, expectedPoints) => {
    if (person.reachedGoal) return { label: 'Meta alcanzada', severity: 'success' };
    if (!Number.isFinite(Number(person.accumulatedPoints)) || Number(person.accumulatedPoints) <= 0) {
        return { label: 'Sin datos', severity: 'secondary' };
    }
    if (expectedPoints === null || expectedPoints === undefined) return { label: 'Sin datos', severity: 'secondary' };
    return Number(person.accumulatedPoints) >= Number(expectedPoints)
        ? { label: 'En ritmo', severity: 'success' }
        : { label: 'Atrasado', severity: 'warning' };
};

const OkrPointsRaceBoard = ({ selectedCycle, scoreboard = [], currentWeek, teams = [], keyResults = [], users = [] }) => {
    const overlayRef = useRef(null);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const pointsBase = Number(selectedCycle?.pointsBase || 1000);
    const numberOfWeeks = Number(selectedCycle?.numberOfWeeks || 0);
    const currentWeekNumber = Number(currentWeek?.weekNumber || 0);
    const expectedPoints = numberOfWeeks > 0 && currentWeekNumber > 0 ? pointsBase * (currentWeekNumber / numberOfWeeks) : null;

    const teamColorMap = useMemo(() => {
        // Use the complete team roster so filtering or ranking changes cannot recolor a team.
        const roster = new Map(teams.map(team => [normalizeId(team), team.name || normalizeId(team)]));
        scoreboard.forEach((row, index) => {
            const teamId = normalizeId(row?.team) || normalizeId(row?.teamId) || `team-${index}`;
            if (!roster.has(teamId)) roster.set(teamId, row?.team?.name || teamId);
        });
        return Object.fromEntries([...roster].sort(([leftId, leftName], [rightId, rightName]) => leftName.localeCompare(rightName, 'es') || leftId.localeCompare(rightId))
            .map(([teamId], index) => [teamId, TEAM_COLORS[index % TEAM_COLORS.length]]));
    }, [scoreboard, teams]);

    const usersMap = useMemo(() => {
        const map = {};
        const addUser = (user) => {
            const userId = normalizeId(user);
            if (!userId) return;
            const currentName = getObjectUserName(map[userId]);
            const nextName = getObjectUserName(user);
            if (!map[userId] || (!currentName && nextName)) map[userId] = user;
        };

        users.forEach(addUser);

        teams.forEach((team) => {
            addUser(team?.managerId);
            (Array.isArray(team?.memberIds) ? team.memberIds : []).forEach(addUser);
        });

        keyResults.forEach((keyResult) => addUser(keyResult?.ownerId));

        scoreboard.forEach((teamRow) => {
            (Array.isArray(teamRow?.members) ? teamRow.members : []).forEach((member) => {
                addUser(member?.user || member?.userId || member);
                const userId = normalizeId(member?.userId || member?.user);
                if (!userId) return;
                const memberName = getObjectUserName(member);
                if (memberName && !getObjectUserName(map[userId])) {
                    map[userId] = { ...member, _id: userId, name: memberName };
                }
            });
        });

        return map;
    }, [keyResults, scoreboard, teams, users]);

    const people = useMemo(() => {
        const rows = [];

        scoreboard.forEach((teamRow, teamIndex) => {
            const team = teamRow.team || {};
            const teamId = normalizeId(team) || normalizeId(teamRow.teamId) || `team-${teamIndex}`;
            const teamName = team.name || `Equipo ${teamIndex + 1}`;
            const color = teamColorMap[teamId] || TEAM_COLORS[teamIndex % TEAM_COLORS.length];

            (teamRow.members || []).forEach((member, memberIndex) => {
                const memberId = normalizeId(member?.userId || member?.user || member);
                if (memberId.startsWith('virtual:')) return;
                const rawAccumulatedPoints = member?.accumulatedPoints;
                const accumulatedPoints = Math.max(0, getFiniteNumber(rawAccumulatedPoints, 0));
                const assignedPoints = Math.max(0, getFiniteNumber(member?.assignedPoints, 0));
                const progressPercentage = pointsBase > 0 ? (accumulatedPoints / pointsBase) * 100 : 0;
                const displayName = getUserDisplayName(member?.user || member?.userId || member, usersMap);
                const tokenPositionPoints = getTokenPositionPoints({ accumulatedPoints }, selectedCycle);
                const cellIndex = Math.min(Math.round(pointsBase / BOARD_STEP), Math.max(0, Math.round(tokenPositionPoints / BOARD_STEP)));
                const initials = getInitials(displayName);

                warnInDevelopment(
                    (rawAccumulatedPoints === null || rawAccumulatedPoints === undefined) && assignedPoints > 0,
                    'La ficha tiene puntos asignados pero no trae accumulatedPoints; se ubicará en Inicio hasta que existan puntos acumulados.',
                    { member, teamName, assignedPoints }
                );
                warnInDevelopment(!memberId, 'No se pudo resolver el owner de la ficha.', { member, teamName });
                warnInDevelopment(displayName === 'Usuario sin nombre', 'No se pudo resolver el nombre visible de la ficha.', { member, memberId, teamName });

                rows.push({
                    id: `${teamId}-${memberId || memberIndex}`,
                    name: displayName,
                    teamName,
                    color,
                    initials,
                    accumulatedPoints,
                    assignedPoints,
                    progressPercentage,
                    configuredWeeklyPoints: numberOfWeeks > 0 ? assignedPoints / numberOfWeeks : 0,
                    tokenPositionPoints,
                    cellIndex,
                    reachedGoal: accumulatedPoints >= pointsBase
                });
            });
        });

        return rows;
    }, [numberOfWeeks, pointsBase, scoreboard, selectedCycle, teamColorMap, usersMap]);

    const boardCells = useMemo(() => {
        const maxIndex = Math.round(pointsBase / BOARD_STEP);
        return Array.from({ length: maxIndex + 1 }, (_, index) => ({
            index,
            value: index * BOARD_STEP
        }));
    }, [pointsBase]);

    const boardRows = useMemo(
        () =>
            chunk(boardCells, BOARD_COLUMNS).map((row, rowIndex) => ({
                rowIndex,
                cells: rowIndex % 2 === 0 ? row : [...row].reverse()
            })),
        [boardCells]
    );

    const peopleByCell = useMemo(
        () =>
            people.reduce((map, person) => {
                const list = map[person.cellIndex] || [];
                list.push(person);
                map[person.cellIndex] = list;
                return map;
            }, {}),
        [people]
    );

    const expectedCellIndex = expectedPoints !== null && pointsBase > 0
        ? Math.min(Math.round(pointsBase / BOARD_STEP), Math.max(0, Math.round(expectedPoints / BOARD_STEP)))
        : null;

    const openPersonSummary = (event, person) => {
        setSelectedPerson(person);
        overlayRef.current?.toggle(event);
    };

    if (!scoreboard.length) {
        return (
            <div className="surface-card border-round p-4 shadow-1 text-center">
                <i className="pi pi-chart-line text-4xl text-primary mb-3" />
                <h3 className="mt-0 mb-2">No hay puntos acumulados para este ciclo todavía.</h3>
                <p className="text-color-secondary m-0">La carrera se activará cuando existan resultados y actualizaciones semanales.</p>
            </div>
        );
    }

    if (!people.length) {
        return (
            <div className="surface-card border-round p-4 shadow-1 text-center">
                <i className="pi pi-users text-4xl text-primary mb-3" />
                <h3 className="mt-0 mb-2">Todavía no hay colaboradores con resultados clave asignados.</h3>
                <p className="text-color-secondary m-0">Asigna responsables a los resultados clave para ver sus fichas en la carrera.</p>
            </div>
        );
    }

    return (
        <div className="surface-card border-round p-3 shadow-1">
            <div className="flex flex-column lg:flex-row lg:align-items-start lg:justify-content-between gap-3 mb-3">
                <div>
                    <h3 className="mt-0 mb-1">Carrera a la meta OKR</h3>
                    <p className="text-color-secondary m-0">Cada ficha avanza hacia la Meta {formatNumber(pointsBase)} puntos según los puntos acumulados.</p>
                </div>
                {expectedPoints !== null ? (
                    <Tag value={`Meta esperada semana actual: ${formatNumber(expectedPoints)} pts`} severity="info" />
                ) : null}
            </div>

            <div className="overflow-x-auto">
                <div
                    className="border-round p-4"
                    style={{
                        minWidth: '1120px',
                        background: 'radial-gradient(circle at top left, #fff7ed 0%, #f7fee7 38%, #eef6ff 100%)',
                        border: '1px solid #dbe4ee',
                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.72)'
                    }}
                >
                    <div className="flex flex-column" style={{ gap: '1.55rem' }}>
                        {boardRows.map(({ rowIndex, cells }) => (
                            <div
                                className="relative"
                                key={rowIndex}
                            >
                                <div
                                className="grid relative"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${BOARD_COLUMNS}, minmax(136px, 1fr))`,
                                    gap: '0.24rem',
                                    zIndex: 2
                                }}
                            >
                                {cells.map((cell, visualIndex) => {
                                    const tokenList = peopleByCell[cell.index] || [];
                                    const isStart = cell.index === 0;
                                    const isGoal = cell.value >= pointsBase;
                                    const isMajorMilestone = cell.value % 250 === 0;
                                    const isExpected = expectedCellIndex === cell.index;
                                    const cellTone = isGoal
                                        ? 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)'
                                        : isStart
                                        ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
                                        : isMajorMilestone
                                        ? 'linear-gradient(135deg, #fed7aa 0%, #fbbf24 100%)'
                                        : CELL_PALETTE[(cell.index + rowIndex) % CELL_PALETTE.length];
                                    const isRowStart = visualIndex === 0;
                                    const isRowEnd = visualIndex === cells.length - 1;

                                    return (
                                        <div
                                            className="relative border-round p-2 shadow-1"
                                            style={{
                                                minHeight: '104px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                background: cellTone,
                                                border: isExpected ? '3px solid #f59e0b' : '3px solid rgba(255,255,255,0.92)',
                                                borderRadius: isRowStart || isRowEnd ? '18px' : '10px',
                                                boxShadow: isExpected
                                                    ? '0 9px 18px rgba(245, 158, 11, 0.22), inset 0 0 0 1px rgba(146, 64, 14, 0.2)'
                                                    : '0 8px 15px rgba(15, 23, 42, 0.09), inset 0 0 0 1px rgba(15, 23, 42, 0.04)'
                                            }}
                                            key={cell.index}
                                        >
                                            <div className="flex align-items-start justify-content-between gap-2">
                                                <div className={`font-bold ${isGoal ? 'text-white' : 'text-color'}`}>
                                                    {isStart ? 'Inicio' : isGoal ? 'Meta' : formatNumber(cell.value)}
                                                </div>
                                                {isExpected ? (
                                                    <span className="text-xs font-semibold border-round px-2 py-1" style={{ color: '#92400e', background: '#fffbeb' }} title={`Meta esperada semana actual: ${formatNumber(expectedPoints)} pts`}>
                                                        <i className="pi pi-flag mr-1" />
                                                        S{currentWeekNumber}
                                                    </span>
                                                ) : null}
                                            </div>
                                            {isGoal ? <div className="text-xs text-white mt-1">{formatNumber(pointsBase)} pts</div> : null}
                                            {isMajorMilestone && !isStart && !isGoal ? <div className="text-xs text-color-secondary mt-1">Hito</div> : null}

                                            <div className="flex flex-wrap align-items-end gap-1" style={{ marginTop: 'auto', paddingTop: '0.4rem' }}>
                                                {tokenList.map((person, tokenIndex) => {
                                                    const tokenSize = tokenList.length > 8 ? 24 : tokenList.length > 4 ? 28 : 32;
                                                    const title = `${person.name}\nEquipo: ${person.teamName}\nPuntos acumulados: ${formatNumber(person.accumulatedPoints)} / ${formatNumber(pointsBase)}\nAvance: ${formatNumber(person.progressPercentage)}%\nPuntos asignados: ${formatNumber(person.assignedPoints)}`;
                                                    return (
                                                        <button
                                                            type="button"
                                                            className="flex flex-column align-items-center border-none bg-transparent p-0 cursor-pointer"
                                                            title={title}
                                                            aria-label={title}
                                                            onClick={(event) => openPersonSummary(event, person)}
                                                            style={{
                                                                transform: `translate(${(tokenIndex % 4) * 2}px, ${(tokenIndex % 2) * -5}px)`,
                                                                marginRight: tokenList.length > 7 ? '-0.12rem' : 0
                                                            }}
                                                            key={person.id}
                                                        >
                                                            <RaceHorseMarker person={person} size={tokenSize} facingRight={rowIndex % 2 === 0} />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                                </div>
                                {rowIndex < boardRows.length - 1 ? (
                                    <div
                                        className="absolute flex align-items-center justify-content-center font-bold text-white shadow-2"
                                        style={{
                                            width: '58px',
                                            height: '58px',
                                            borderRadius: '999px',
                                            background: 'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)',
                                            right: rowIndex % 2 === 0 ? '-0.75rem' : 'auto',
                                            left: rowIndex % 2 === 0 ? 'auto' : '-0.75rem',
                                            bottom: '-2.05rem',
                                            zIndex: 1,
                                            border: '5px solid rgba(255,255,255,0.92)'
                                        }}
                                    >
                                        {rowIndex % 2 === 0 ? '↴' : '↙'}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex align-items-center flex-wrap gap-3 mt-3">
                {scoreboard.map((row, index) => {
                    const teamId = normalizeId(row?.team) || normalizeId(row?.teamId) || `team-${index}`;
                    return (
                        <div className="flex align-items-center gap-2 text-sm" key={teamId}>
                            <span className="border-circle" style={{ width: '12px', height: '12px', background: teamColorMap[teamId] }} />
                            <span>{row?.team?.name || `Equipo ${index + 1}`}</span>
                        </div>
                    );
                })}
            </div>

            <OverlayPanel ref={overlayRef} dismissable showCloseIcon>
                {selectedPerson ? (
                    <div style={{ width: 'min(320px, 82vw)' }}>
                        <div className="flex align-items-center gap-3 mb-3">
                            <RaceHorseMarker person={selectedPerson} size={38} />
                            <div>
                                <div className="font-bold">{selectedPerson.name}</div>
                                <div className="text-sm text-color-secondary">{selectedPerson.teamName}</div>
                            </div>
                        </div>

                        <div className="flex flex-column gap-2 text-sm">
                            <div className="flex justify-content-between gap-3">
                                <span className="text-color-secondary">Colaborador</span>
                                <span className="font-medium text-right">{selectedPerson.name}</span>
                            </div>
                            <div className="flex justify-content-between gap-3">
                                <span className="text-color-secondary">Equipo</span>
                                <span className="font-medium text-right">{selectedPerson.teamName}</span>
                            </div>
                            <div className="flex justify-content-between gap-3">
                                <span className="text-color-secondary">Puntos acumulados</span>
                                <span className="font-medium text-right">{formatNumber(selectedPerson.accumulatedPoints)} / {formatNumber(pointsBase)}</span>
                            </div>
                            <div className="flex justify-content-between gap-3">
                                <span className="text-color-secondary">Avance</span>
                                <span className="font-medium text-right">{formatNumber(selectedPerson.progressPercentage)}%</span>
                            </div>
                            <div className="flex justify-content-between gap-3">
                                <span className="text-color-secondary">Puntos asignados</span>
                                <span className="font-medium text-right">{formatNumber(selectedPerson.assignedPoints)}</span>
                            </div>
                            <div className="flex justify-content-between gap-3">
                                <span className="text-color-secondary">Meta del ciclo</span>
                                <span className="font-medium text-right">{formatNumber(pointsBase)} pts</span>
                            </div>
                            <div className="flex justify-content-between gap-3">
                                <span className="text-color-secondary">Puntos/semana configurados</span>
                                <span className="font-medium text-right">{formatNumber(selectedPerson.configuredWeeklyPoints)}</span>
                            </div>
                            <div className="flex align-items-center justify-content-between gap-3 pt-2">
                                <span className="text-color-secondary">Estado</span>
                                {(() => {
                                    const status = getPaceStatus(selectedPerson, expectedPoints);
                                    return <Tag value={status.label} severity={status.severity} />;
                                })()}
                            </div>
                        </div>
                    </div>
                ) : null}
            </OverlayPanel>
        </div>
    );
};

export default OkrPointsRaceBoard;
